import React, { useState, useEffect } from "react";
import { Calendar, Hourglass, Wrench, Building, X } from 'lucide-react';
import appointmentService from "../services/appointmentService";
import AppointmentCalendar from "../components/AppointmentCalendar";
import "../styles/appointments.css";

const AdminAppointmentPanel = () => {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all, pending, tech_lab, aksaray, calendar
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchAppointments();
  }, [activeTab, filterType, filterStatus]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError("");

      if (activeTab === "calendar") {
        return; // Takvim kendi verilerini yüklüyor
      }

      let typeFilter = filterType === "all" ? null : filterType;
      let statusFilter = null;

      if (activeTab === "pending") {
        typeFilter = null;
        statusFilter = "pending";
      } else if (activeTab === "tech_lab") {
        typeFilter = "tech_lab";
        statusFilter = filterStatus === "all" ? null : filterStatus;
      } else if (activeTab === "aksaray") {
        typeFilter = "aksaray_building";
        statusFilter = filterStatus === "all" ? null : filterStatus;
      } else if (activeTab === "all") {
        statusFilter = filterStatus === "all" ? null : filterStatus;
      }

      const response = await appointmentService.getAllAppointments(typeFilter, statusFilter, token);
      setAppointments(response.appointments || []);
      setFilteredAppointments(response.appointments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (appointmentId) => {
    try {
      setLoading(true);
      await appointmentService.approveAppointment(appointmentId, token);
      setSuccess("Randevu onaylandı!");
      setSelectedAppointment(null);
      fetchAppointments();
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (appointmentId) => {
    if (!rejectReason.trim()) {
      alert("Lütfen reddetme nedenini yazınız");
      return;
    }

    try {
      setLoading(true);
      await appointmentService.rejectAppointment(appointmentId, rejectReason, token);
      setSuccess("Randevu reddedildi!");
      setRejectReason("");
      setSelectedAppointment(null);
      fetchAppointments();
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAppointmentTitle = (apt) => {
    if (apt.full_name) return apt.full_name;
    return `${apt.first_name} ${apt.last_name}`;
  };

  const getAppointmentType = (apt) => {
    if (apt.type === "tech_lab") return "Teknoloji Atölyesi";
    if (apt.type === "aksaray_building") return "Aksaray Binası";
    return "Bilinmiyor";
  };

  return (
    <div className="admin-appointment-panel">
      <div className="panel-header">
        <h2><Calendar className="inline-icon"/> Randevu Yönetim Paneli</h2>
        <p>Tüm randevuları yönetin, onaylayın ve reddedin</p>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="admin-tabs">
        <button
          className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          Tümü
        </button>
        <button
          className={`tab-btn ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          <Hourglass className="inline-icon"/> Beklemede
        </button>
        <button
          className={`tab-btn ${activeTab === "tech_lab" ? "active" : ""}`}
          onClick={() => setActiveTab("tech_lab")}
        >
          <Wrench className="inline-icon"/> Teknoloji Atölyesi
        </button>
        <button
          className={`tab-btn ${activeTab === "aksaray" ? "active" : ""}`}
          onClick={() => setActiveTab("aksaray")}
        >
          <Building className="inline-icon"/> Aksaray Binası
        </button>
        <button
          className={`tab-btn ${activeTab === "calendar" ? "active" : ""}`}
          onClick={() => setActiveTab("calendar")}
        >
          <Calendar className="inline-icon"/> Takvim
        </button>
      </div>

      {activeTab === "calendar" ? (
        <div className="tab-content">
          <AppointmentCalendar />
        </div>
      ) : (
        <div className="tab-content">
          <div className="filter-bar">
            {activeTab === "all" && (
              <>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">Tüm Türler</option>
                  <option value="tech_lab">Teknoloji Atölyesi</option>
                  <option value="aksaray_building">Aksaray Binası</option>
                </select>
              </>
            )}

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="pending">Beklemede</option>
              <option value="approved">Onaylı</option>
              <option value="rejected">Reddedilmiş</option>
              <option value="cancelled">İptal</option>
            </select>
          </div>

          {loading ? (
            <div className="loading">Yükleniyor...</div>
          ) : filteredAppointments.length === 0 ? (
            <div className="empty-message">Hiç randevu bulunamadı</div>
          ) : (
            <div className="admin-appointments-table">
              <div className="table-header">
                <div className="col-name">Ad</div>
                <div className="col-type">Tür</div>
                <div className="col-date">Tarih & Saat</div>
                <div className="col-status">Durum</div>
                <div className="col-actions">İşlemler</div>
              </div>

              {filteredAppointments.map(apt => (
                <div key={apt._id} className={`table-row status-${apt.status}`}>
                  <div className="col-name">{getAppointmentTitle(apt)}</div>
                  <div className="col-type">{getAppointmentType(apt)}</div>
                  <div className="col-date">
                    {apt.appointment_date} {apt.appointment_time}
                  </div>
                  <div className="col-status">
                    <span className={`status-badge ${apt.status}`}>{apt.status}</span>
                  </div>
                  <div className="col-actions">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => setSelectedAppointment(apt)}
                    >
                      Detay
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal: Randevu Detayları ve İşlemleri */}
      {selectedAppointment && (
        <div className="modal-overlay" onClick={() => setSelectedAppointment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{getAppointmentTitle(selectedAppointment)}</h3>
              <button
                className="close-btn"
                onClick={() => setSelectedAppointment(null)}
              >
                <X />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <strong>Tür:</strong> {getAppointmentType(selectedAppointment)}
              </div>
              <div className="detail-row">
                <strong>Tarih:</strong> {selectedAppointment.appointment_date}
              </div>
              <div className="detail-row">
                <strong>Saat:</strong> {selectedAppointment.appointment_time}
              </div>
              <div className="detail-row">
                <strong>Katılımcı Sayısı:</strong> {selectedAppointment.participant_count}
              </div>
              <div className="detail-row">
                <strong>Durum:</strong>{" "}
                <span className={`status-badge ${selectedAppointment.status}`}>
                  {selectedAppointment.status}
                </span>
              </div>

              {selectedAppointment.purpose && (
                <div className="detail-row">
                  <strong>Amaç:</strong> {selectedAppointment.purpose}
                </div>
              )}
              {selectedAppointment.event_type && (
                <div className="detail-row">
                  <strong>Etkinlik Türü:</strong> {selectedAppointment.event_type}
                </div>
              )}
              {selectedAppointment.requested_room && (
                <div className="detail-row">
                  <strong>Talep Edilen Salon:</strong> {selectedAppointment.requested_room}
                </div>
              )}
              {selectedAppointment.phone && (
                <div className="detail-row">
                  <strong>Telefon:</strong> {selectedAppointment.phone}
                </div>
              )}
              {selectedAppointment.email && (
                <div className="detail-row">
                  <strong>E-posta:</strong> {selectedAppointment.email}
                </div>
              )}

              {selectedAppointment.status === "pending" && (
                <div className="action-section">
                  <h4>İşlem Seçiniz</h4>
                  <div className="action-buttons">
                    <button
                      className="btn btn-success"
                      onClick={() => handleApprove(selectedAppointment._id)}
                      disabled={loading}
                    >
                      ✓ Onayla
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        if (rejectReason === "") {
                          // Açılmamışsa açılsın
                          setRejectReason("placeholder");
                        }
                      }}
                    >
                      ✕ Reddet
                    </button>
                  </div>

                  {rejectReason && (
                    <div className="reject-reason-section">
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Reddetme nedenini açıklayınız..."
                        rows="3"
                        className="reject-textarea"
                      />
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReject(selectedAppointment._id)}
                        disabled={loading}
                      >
                        Reddet
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setRejectReason("")}
                      >
                        İptal
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAppointmentPanel;
